import React from "react";
import clsx from "clsx";
import ReactECharts from "echarts-for-react";
import { ReactComponent as OperationsIcon } from "./icons/operations-icon.svg";
import { ReactComponent as StatusInfoIcon } from "./icons/status-info-information.svg";
import { listSites } from "../../api/sites";
import { fetchTrackerStatus } from "../../api/modbus";

const REFRESH_MS = 20000;
const STALE_WARNING_SECONDS = 240;
const STALE_CRITICAL_SECONDS = 300;

const formatTimestamp = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString();
};

const formatAngle = (value) => {
  if (value === null || typeof value === "undefined") return "—";
  return `${Number(value).toFixed(1)}°`;
};

const formatPercent = (value) => {
  if (value === null || typeof value === "undefined") return "—";
  return `${Math.round(Number(value))}%`;
};

const severityClasses = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  red: "bg-rose-50 text-rose-700 border-rose-200",
  slate: "bg-slate-50 text-slate-600 border-slate-200",
};

const gaugeStroke = {
  green: "#10b981",
  amber: "#f59e0b",
  red: "#ef4444",
  slate: "#cbd5e1",
};

const statusIconToneClass = {
  green: "text-emerald-600",
  amber: "text-amber-500",
  red: "text-rose-500",
  slate: "text-slate-400",
};

const kpiBarToneClass = {
  green: "bg-emerald-500",
  amber: "bg-amber-500",
  red: "bg-rose-500",
  blue: "bg-blue-500",
  indigo: "bg-indigo-600",
  slate: "bg-slate-300",
};

function updateAgeState(value) {
  if (!value) return { seconds: null, tone: "red" };
  const seconds = Math.max(0, (Date.now() - new Date(value).getTime()) / 1000);
  if (seconds > STALE_CRITICAL_SECONDS) return { seconds, tone: "red" };
  if (seconds > STALE_WARNING_SECONDS) return { seconds, tone: "amber" };
  return { seconds, tone: "slate" };
}

function trackerStatusTone(tracker) {
  if (tracker.communication_error_active || (tracker.alarm?.value ?? 0) > 0) return "red";
  const statusValue = tracker.status?.value;
  if (statusValue === 10) return "green";
  if (statusValue && statusValue !== 0) return "amber";
  return "slate";
}

function communicationTone(tracker) {
  if (tracker.communication_state === "offline") return "red";
  if (tracker.communication_state === "stale") return "amber";
  if (tracker.communication_state === "ok") return "green";
  return tracker.communication_error_active ? "red" : "green";
}

function clampLabelDegree(value) {
  return Math.max(0, Math.round(value));
}

function buildAngleBuckets(maxAngle) {
  const safeMax = Math.max(Math.abs(maxAngle || 0), 1);
  const edge = safeMax * 0.66;
  const inner = safeMax * 0.22;
  const edgeLabel = clampLabelDegree(edge);
  const innerLabel = clampLabelDegree(inner);
  return [
    { key: "far_west", label: `< -${edgeLabel}°`, tone: "blue", matches: (v) => v < -edge },
    { key: "west", label: `-${edgeLabel}/${innerLabel}°`, tone: "indigo", matches: (v) => v >= -edge && v < -inner },
    { key: "center", label: `-${innerLabel}/${innerLabel}°`, tone: "green", matches: (v) => v >= -inner && v <= inner },
    { key: "east", label: `${innerLabel}/${edgeLabel}°`, tone: "indigo", matches: (v) => v > inner && v <= edge },
    { key: "far_east", label: `> ${edgeLabel}°`, tone: "blue", matches: (v) => v > edge },
  ];
}

function countBuckets(values, buckets) {
  const counts = buckets.map((bucket) => ({ ...bucket, count: 0 }));
  let missing = 0;
  values.forEach((value) => {
    if (value == null || Number.isNaN(Number(value))) {
      missing += 1;
      return;
    }
    const numeric = Number(value);
    const target = counts.find((bucket) => bucket.matches(numeric));
    if (target) target.count += 1;
  });
  return { buckets: counts, missing };
}

function communicationBucket(timestamp) {
  if (!timestamp) return "missing";
  const ageSeconds = Math.max(0, (Date.now() - new Date(timestamp).getTime()) / 1000);
  if (ageSeconds > STALE_CRITICAL_SECONDS) return "offline";
  if (ageSeconds > STALE_WARNING_SECONDS) return "stale";
  return "online";
}

function SummaryBarCard({ title, subtitle, buckets, missingCount = 0 }) {
  const maxCount = Math.max(1, ...buckets.map((bucket) => bucket.count));
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</div>
      {subtitle ? <div className="mt-1 text-xs text-slate-500">{subtitle}</div> : null}
      <div className="mt-3 grid grid-cols-5 gap-2">
        {buckets.map((bucket) => {
          const heightPct = bucket.count > 0 ? Math.max(16, Math.round((bucket.count / maxCount) * 100)) : 6;
          return (
            <div key={bucket.key} className="flex flex-col items-center gap-1">
              <div className="text-sm font-semibold text-slate-800">{bucket.count}</div>
              <div className="flex h-14 w-full items-end justify-center rounded-md bg-white px-1 py-1">
                <div
                  className={clsx("w-full rounded-sm", kpiBarToneClass[bucket.tone] || kpiBarToneClass.slate)}
                  style={{ height: `${heightPct}%` }}
                />
              </div>
              <div className="text-center text-[11px] leading-4 text-slate-500">{bucket.label}</div>
            </div>
          );
        })}
      </div>
      {missingCount > 0 ? (
        <div className="mt-2 text-[11px] text-slate-500">Unavailable: {missingCount}</div>
      ) : null}
    </div>
  );
}

function TrackerGauge({ currentAngle, targetAngle, maxAngle, state }) {
  const safeMax = Math.max(Math.abs(maxAngle || 0), 1);
  const currentValue = Math.max(-safeMax, Math.min(safeMax, Number(currentAngle || 0)));
  const targetValue = Math.max(-safeMax, Math.min(safeMax, Number(targetAngle || 0)));
  const colorKey = state || "slate";
  const colorHex = gaugeStroke[colorKey];
  const option = {
    animation: false,
    series: [
      {
        type: "gauge",
        startAngle: 180,
        endAngle: 0,
        min: -safeMax,
        max: safeMax,
        center: ["50%", "86%"],
        radius: "98%",
        splitNumber: 4,
        axisLine: {
          lineStyle: {
            width: 8,
            color: [[1, colorHex]],
            cap: "round",
          },
        },
        axisTick: {
          distance: -10,
          splitNumber: 4,
          length: 4,
          lineStyle: {
            color: "#94a3b8",
            width: 1,
          },
        },
        splitLine: {
          distance: -11,
          length: 8,
          lineStyle: {
            color: "#64748b",
            width: 1.5,
          },
        },
        axisLabel: {
          distance: -34,
          color: "#64748b",
          fontSize: 9,
          formatter: (value) => {
            if (Math.abs(value + safeMax) < 0.001) return `W ${safeMax}°`;
            if (Math.abs(value) < 0.001) return "0°";
            if (Math.abs(value - safeMax) < 0.001) return `E ${safeMax}°`;
            return "";
          },
        },
        pointer: {
          show: true,
          length: "64%",
          width: 3,
          offsetCenter: [0, "2%"],
          icon: "path://M-1.8 30 L1.8 30 L1.8 -10 L0 -16 L-1.8 -10 Z",
          itemStyle: {
            color: colorHex,
          },
        },
        anchor: {
          show: true,
          size: 8,
          showAbove: true,
          itemStyle: {
            color: colorHex,
            borderWidth: 0,
          },
        },
        detail: { show: false },
        title: { show: false },
        data: [{ value: currentValue }],
        z: 3,
      },
      {
        type: "gauge",
        startAngle: 180,
        endAngle: 0,
        min: -safeMax,
        max: safeMax,
        center: ["50%", "86%"],
        radius: "98%",
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        pointer: {
          show: true,
          length: "70%",
          width: 2,
          offsetCenter: [0, "2%"],
          icon: "path://M-0.8 34 L0.8 34 L0.8 -22 L-0.8 -22 Z",
          itemStyle: {
            color: "#0f172a",
          },
        },
        anchor: { show: false },
        detail: { show: false },
        title: { show: false },
        data: [{ value: targetValue }],
        z: 4,
      },
    ],
  };

  return <ReactECharts option={option} notMerge lazyUpdate style={{ height: 132, width: "100%" }} />;
}

function BatteryIcon({ value, state }) {
  const pct = Math.max(0, Math.min(100, Number(value || 0)));
  const segments = [25, 50, 75, 100];
  const color =
    state === "red" ? "bg-rose-500" : state === "amber" ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div className="flex items-center gap-2" title={value == null ? "Battery SoC unavailable" : `Battery SoC ${Math.round(pct)}%`}>
      <div className="relative h-5 w-11 rounded-md border-2 border-slate-300 bg-white p-[2px]">
        <div className="flex h-full gap-[2px]">
          {segments.map((threshold) => (
            <div
              key={threshold}
              className={clsx("h-full flex-1 rounded-[2px]", pct >= threshold ? color : "bg-slate-100")}
            />
          ))}
        </div>
        <div className="absolute -right-[5px] top-[4px] h-2.5 w-1.5 rounded-r-sm bg-slate-300" />
      </div>
      <span className="text-xs font-semibold text-slate-700">{formatPercent(value)}</span>
    </div>
  );
}

function TinyBadge({ children, title, tone = "slate" }) {
  return (
    <span
      title={title}
      className={clsx(
        "inline-flex items-center rounded-full border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide",
        severityClasses[tone] || severityClasses.slate
      )}
    >
      {children}
    </span>
  );
}

function StatusIcon({ tone, title, children }) {
  return (
    <div
      title={title}
      className={clsx(
        "flex h-8 w-8 items-center justify-center rounded-full border",
        severityClasses[tone] || severityClasses.slate
      )}
    >
      {children}
    </div>
  );
}

function WindGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M6.25 5.5C6.25 3.705 7.705 2.25 9.5 2.25s3.25 1.455 3.25 3.25S11.295 8.75 9.5 8.75H3A.75.75 0 0 1 3 7.25h6.5a1.75 1.75 0 1 0-1.75-1.75v.357a.75.75 0 0 1-1.5 0V5.5Zm8 2c0-2.347 1.903-4.25 4.25-4.25s4.25 1.903 4.25 4.25-1.903 4.25-4.25 4.25H2a.75.75 0 0 1 0-1.5h16.5a2.75 2.75 0 1 0-2.75-2.75V8a.75.75 0 0 1-1.5 0v-.5ZM3.25 14A.75.75 0 0 1 4 13.25h14.5c2.347 0 4.25 1.903 4.25 4.25s-1.903 4.25-4.25 4.25-4.25-1.903-4.25-4.25V17a.75.75 0 0 1 1.5 0v.5a2.75 2.75 0 1 0 2.75-2.75H4a.75.75 0 0 1-.75-.75Z" />
    </svg>
  );
}

function SignalGlyph() {
  return (
    <svg viewBox="0 0 96 96" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M48 60a12 12 0 1 0 12 12 12.008 12.008 0 0 0-12-12Z" />
      <path d="M22.606 46.629a6 6 0 1 0 8.507 8.461 24.226 24.226 0 0 1 33.773 0 5.951 5.951 0 0 0 4.254 1.77 6 6 0 0 0 4.254-10.23c-13.617-13.712-37.171-13.712-50.788 0Z" />
      <path d="M90.27 29.777a59.141 59.141 0 0 0-84.539 0 5.999 5.999 0 1 0 8.531 8.438c18.117-18.328 49.359-18.328 67.477 0a5.999 5.999 0 1 0 8.531-8.438Z" />
    </svg>
  );
}

function HeaderWindState({ speed, directionLabel, directionDeg, severity, resumeSpeed, stowSpeed }) {
  const tone = severity || "slate";
  const titleParts = [];
  if (resumeSpeed != null) titleParts.push(`Resume threshold ${resumeSpeed} m/s`);
  if (stowSpeed != null) titleParts.push(`Stow threshold ${stowSpeed} m/s`);

  return (
    <div className={clsx("rounded-2xl border px-4 py-3", severityClasses[tone] || severityClasses.slate)}>
      <div className="text-xs font-semibold uppercase tracking-wide">Wind</div>
      <div className="mt-1 text-lg font-semibold">{speed == null ? "—" : `${Number(speed).toFixed(1)} m/s`}</div>
      <div className="text-sm">
        Wind direction: {directionDeg == null ? "—" : `${Math.round(directionDeg)}°`} {directionLabel ? `- ${directionLabel}` : ""}
      </div>
      {titleParts.length > 0 && <div className="mt-1 text-xs">{titleParts.join(" | ")}</div>}
    </div>
  );
}

function TrackerCard({ tracker, maxAngle }) {
  const statusTone = trackerStatusTone(tracker);
  const alarmTone = tracker.alarm?.value && tracker.alarm.value > 0 ? "red" : "slate";
  const commTone = communicationTone(tracker);
  const statusTitle = tracker.status?.label
    ? `Tracker status: ${tracker.status.label}`
    : tracker.status?.value != null
      ? `Tracker status value ${tracker.status.value}`
      : "Tracker status unavailable";
  const windTitle = tracker.wind_mode_active
    ? `Wind-related tracker status active${tracker.status?.label ? `: ${tracker.status.label}` : ""}`
    : "No wind-mode indication from tracker status";
  const communicationTitle = tracker.last_comm_timestamp
    ? `Last successful communication ${formatTimestamp(tracker.last_comm_timestamp)}${
        tracker.communication_age_seconds != null
          ? ` (${(tracker.communication_age_seconds / 60).toFixed(1)} minutes ago)`
          : ""
      }${tracker.communication_state ? ` — state: ${tracker.communication_state}` : ""}`
    : tracker.communication_error_active
      ? `Communication problem active${tracker.alarm?.label ? `: ${tracker.alarm.label}` : ""}`
      : "No communication problem indication";
  const freshnessTimestamp = tracker.last_comm_timestamp || tracker.last_timestamp;
  const freshnessLabel = tracker.last_comm_timestamp ? "Last comm" : "Latest update";
  const age = updateAgeState(freshnessTimestamp);

  return (
    <div className="rounded-[26px] border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-slate-900">{tracker.display_name}</div>
          <div className="mt-1 flex flex-wrap gap-2">
            {tracker.array_name && <TinyBadge title={tracker.transformer_name || tracker.array_name}>{tracker.array_name}</TinyBadge>}
            {tracker.alarm?.value != null && tracker.alarm.value > 0 && (
              <TinyBadge title={tracker.alarm?.label || `Alarm ${tracker.alarm.value}`} tone={alarmTone}>
                AL {tracker.alarm.value}
              </TinyBadge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusIcon tone={statusTone} title={statusTitle}>
            {statusTone === "green" ? (
              <OperationsIcon className={clsx("h-4 w-4", statusIconToneClass[statusTone])} aria-hidden="true" />
            ) : (
              <StatusInfoIcon className={clsx("h-4 w-4", statusIconToneClass[statusTone])} aria-hidden="true" />
            )}
          </StatusIcon>
          <StatusIcon tone={commTone} title={communicationTitle}>
            <SignalGlyph />
          </StatusIcon>
          <StatusIcon tone={tracker.wind_mode_active ? "red" : "green"} title={windTitle}>
            <WindGlyph />
          </StatusIcon>
        </div>
      </div>

      <div className="mt-1">
        <TrackerGauge
          currentAngle={tracker.current_angle_deg}
          targetAngle={tracker.target_angle_deg}
          maxAngle={maxAngle}
          state={tracker.angle_state}
        />
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
        <div className="rounded-2xl bg-slate-50 px-3 py-2 text-xs font-medium text-slate-700">
          <span className="font-semibold text-slate-500">Current:</span>{" "}
          <span className="font-semibold text-slate-900">{formatAngle(tracker.current_angle_deg)}</span>
        </div>
        <div className="rounded-2xl bg-slate-50 px-3 py-2 text-xs font-medium text-slate-700">
          <span className="font-semibold text-slate-500">Target:</span>{" "}
          <span className="font-semibold text-slate-900">{formatAngle(tracker.target_angle_deg)}</span>
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <BatteryIcon value={tracker.battery_soc_pct} state={tracker.battery_state} />
        <div
          title={
            tracker.angle_delta_deg != null ? `Angle difference ${tracker.angle_delta_deg.toFixed(1)}°` : "Angle difference unavailable"
          }
          className={clsx(
            "rounded-full border px-2.5 py-1 text-xs font-semibold uppercase tracking-wide",
            severityClasses[tracker.angle_state || "slate"] || severityClasses.slate
          )}
        >
          Delta {tracker.angle_delta_deg != null ? `${tracker.angle_delta_deg.toFixed(1)}°` : "—"}
        </div>
      </div>

      <div
        title={age.seconds != null ? `Value age ${(age.seconds / 60).toFixed(1)} minutes` : "No timestamp available"}
        className={clsx(
          "mt-2 inline-flex rounded-full border px-2 py-1 text-[11px]",
          severityClasses[age.tone] || severityClasses.slate
        )}
      >
        {freshnessLabel}: {formatTimestamp(freshnessTimestamp)}
      </div>
    </div>
  );
}

export default function TrackerStatusPage() {
  const [sites, setSites] = React.useState([]);
  const [selectedSite, setSelectedSite] = React.useState("");
  const [selectedArray, setSelectedArray] = React.useState("all");
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState("");
  const [lastRefreshedAt, setLastRefreshedAt] = React.useState(null);

  const loadTrackerStatus = React.useCallback(
    async (siteId, { silent = false } = {}) => {
      if (!siteId) return;
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError("");
      try {
        const payload = await fetchTrackerStatus(siteId);
        setData(payload);
        setLastRefreshedAt(new Date());
      } catch (err) {
        setError("Unable to load tracker status.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  React.useEffect(() => {
    let ignore = false;
    const load = async () => {
      try {
        const siteRows = await listSites();
        if (ignore) return;
        setSites(siteRows || []);
        if (siteRows && siteRows.length > 0) {
          setSelectedSite((current) => current || String(siteRows[0].id));
        }
      } catch (err) {
        if (!ignore) {
          setError("Unable to load sites.");
        }
      }
    };
    load();
    return () => {
      ignore = true;
    };
  }, []);

  React.useEffect(() => {
    if (!selectedSite) return;
    loadTrackerStatus(selectedSite);
  }, [selectedSite, loadTrackerStatus]);

  React.useEffect(() => {
    if (!selectedSite) return;
    const timer = window.setInterval(() => {
      loadTrackerStatus(selectedSite, { silent: true });
    }, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [selectedSite, loadTrackerStatus]);

  React.useEffect(() => {
    if (!data?.arrays?.some((row) => String(row.array_id) === selectedArray)) {
      setSelectedArray("all");
    }
  }, [data, selectedArray]);

  const filteredTrackers = React.useMemo(() => {
    const rows = data?.trackers || [];
    if (selectedArray === "all") return rows;
    return rows.filter((row) => String(row.array_id) === selectedArray);
  }, [data, selectedArray]);

  const siteInfo = data?.site;
  const header = data?.header;
  const maxAngle = siteInfo?.max_tracker_angle_deg || 60;
  const socSummary = React.useMemo(
    () =>
      countBuckets(filteredTrackers.map((row) => row.battery_soc_pct), [
        { key: "soc_low", label: "0-30%", tone: "red", matches: (v) => v >= 0 && v <= 30 },
        { key: "soc_midlow", label: "31-60%", tone: "amber", matches: (v) => v > 30 && v <= 60 },
        { key: "soc_midhigh", label: "61-80%", tone: "blue", matches: (v) => v > 60 && v <= 80 },
        { key: "soc_high", label: "81-100%", tone: "indigo", matches: (v) => v > 80 && v <= 100 },
      ]),
    [filteredTrackers]
  );
  const angleSummary = React.useMemo(
    () => countBuckets(filteredTrackers.map((row) => row.current_angle_deg), buildAngleBuckets(maxAngle)),
    [filteredTrackers, maxAngle]
  );
  const communicationSummary = React.useMemo(() => {
    const counts = {
      online: 0,
      stale: 0,
      offline: 0,
      missing: 0,
    };
    filteredTrackers.forEach((row) => {
      const bucket = communicationBucket(row.last_comm_timestamp || row.last_timestamp);
      counts[bucket] += 1;
    });
    return {
      buckets: [
        { key: "online", label: "Online", tone: "green", count: counts.online },
        { key: "stale", label: "Stale", tone: "amber", count: counts.stale },
        { key: "offline", label: "Offline", tone: "red", count: counts.offline },
      ],
      missing: counts.missing,
    };
  }, [filteredTrackers]);

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="min-w-[240px]">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tracker status</div>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold text-slate-900">{siteInfo?.name || "Tracker Status"}</h1>
            <select
              className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm"
              value={selectedSite}
              onChange={(event) => setSelectedSite(event.target.value)}
            >
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => loadTrackerStatus(selectedSite, { silent: true })}
              disabled={!selectedSite || refreshing}
              className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>
          <div className="mt-2 text-sm text-slate-600">
            {siteInfo?.location_label || "Location unavailable"}
          </div>
          <div className="mt-2 text-xs text-slate-500">
            Last refreshed: {lastRefreshedAt ? lastRefreshedAt.toLocaleTimeString() : "—"} | Auto-refresh every 20s
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
          <HeaderWindState
            speed={header?.wind_speed_mps}
            directionLabel={header?.wind_dir_label}
            directionDeg={header?.wind_dir_deg}
            severity={header?.wind_severity}
            resumeSpeed={siteInfo?.resume_wind_speed_mps}
            stowSpeed={siteInfo?.stow_wind_speed_mps}
          />
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tracker setup</div>
            <div className="mt-1 text-sm text-slate-700">
              Stow angle: {formatAngle(siteInfo?.stow_position_deg)}
            </div>
            <div className="text-sm text-slate-700">
              Gauge span: W {maxAngle}° / 0° / E {maxAngle}°
            </div>
            <div className="mt-1 text-xs text-slate-500">
              Latest wind sample: {formatTimestamp(header?.last_timestamp)}
            </div>
          </div>
          <SummaryBarCard
            title="Battery SoC"
            subtitle={`Trackers in SoC ranges${selectedArray !== "all" ? " for selected array" : ""}`}
            buckets={socSummary.buckets}
            missingCount={socSummary.missing}
          />
          <SummaryBarCard
            title="Tracker Angle"
            subtitle={`Distribution by current angle${selectedArray !== "all" ? " for selected array" : ""}`}
            buckets={angleSummary.buckets}
            missingCount={angleSummary.missing}
          />
          <SummaryBarCard
            title="Communication"
            subtitle="TRKLASTCOM based, offline after 300s"
            buckets={communicationSummary.buckets}
            missingCount={communicationSummary.missing}
          />
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setSelectedArray("all")}
            className={clsx(
              "rounded-full border px-3 py-1.5 text-sm font-medium",
              selectedArray === "all"
                ? "border-blue-200 bg-blue-50 text-blue-700"
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            )}
          >
            All
          </button>
          {(data?.arrays || []).map((array) => (
            <button
              key={array.array_id}
              type="button"
              title={array.transformer_name || array.array_name}
              onClick={() => setSelectedArray(String(array.array_id))}
              className={clsx(
                "rounded-full border px-3 py-1.5 text-sm font-medium",
                selectedArray === String(array.array_id)
                  ? "border-blue-200 bg-blue-50 text-blue-700"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              )}
            >
              {array.array_name}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      {loading ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm">
          Loading tracker status...
        </div>
      ) : filteredTrackers.length === 0 ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm">
          No tracker data available for the selected site or array filter.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
          {filteredTrackers.map((tracker) => (
            <TrackerCard key={tracker.tracker_key} tracker={tracker} maxAngle={maxAngle} />
          ))}
        </div>
      )}
    </div>
  );
}
